import { Component, ElementRef, EventEmitter, HostListener, OnDestroy, Output, ViewChild, Inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../services/auth.service';
import { GlobalSearchComponent } from "../../../shared/components/global-search/global-search.component";
import { AccessibilityMenuComponent } from "../../../shared/components/accessibility-menu/accessibility-menu.component";
import { RoutesPath } from '../../../shared/constants/routes-path';
import { environment } from '../../../../environments/environment';
import { DOCUMENT, NgClass } from '@angular/common';
import { NotificationHistoryService } from '../../../shared/services/notification-history.service';
import { SharedService } from '../../../shared/services/shared.service';
import { Subscription } from 'rxjs';
import {
  AccessibilitySettings,
  TextSpacingLevel,
  LineSpacingLevel,
  FontWeightOption,
} from '../../../shared/models/accessibility-settings.model';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [TranslateModule, GlobalSearchComponent, AccessibilityMenuComponent, RouterLink, NgClass],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
})
export class HeaderComponent implements OnDestroy {
  // store = inject(Store);
  user: any = ""; //toSignal(this.store.select(selectUser), { initialValue: null });
  selectedLang: any = "en";
  private html = document.documentElement;
  private readonly accessibilityStorageKey = 'dc-accessibility-settings';
  private readonly defaultAccessibilitySettings: AccessibilitySettings = {
    fontScale: 100,
    highContrast: false,
    grayscale: false,
    underlineLinks: false,
    readingGuide: false,
    bigCursor: false,
    textSpacing: 'normal',
    lineSpacing: 'normal',
    fontWeight: 'normal',
  };
  private readonly textSpacingLevels: TextSpacingLevel[] = ['normal', 'medium', 'large'];
  private readonly lineSpacingLevels: LineSpacingLevel[] = ['normal', 'medium', 'large'];
  private readonly textSpacingStyles: Record<TextSpacingLevel, { letterSpacing: string; wordSpacing: string }> = {
    normal: { letterSpacing: 'normal', wordSpacing: 'normal' },
    medium: { letterSpacing: '0.08em', wordSpacing: '0.16em' },
    large: { letterSpacing: '0.16em', wordSpacing: '0.32em' },
  };
  private readonly lineSpacingStyles: Record<LineSpacingLevel, string> = {
    normal: 'normal',
    medium: '1.85',
    large: '2.2',
  };
  private readonly fontWeightClass: Record<FontWeightOption, boolean> = {
    normal: false,
    bold: true,
  };
  private readingGuideOverlay?: HTMLDivElement;
  private readingGuideHighlight?: HTMLDivElement;
  private pointerMoveHandler?: (event: PointerEvent) => void;
  private speechUtterance?: SpeechSynthesisUtterance;
  private readonly speechSynthesisRef: SpeechSynthesis | null =
    typeof window !== 'undefined' && 'speechSynthesis' in window ? window.speechSynthesis : null;
  private speechVoices: SpeechSynthesisVoice[] = [];
  private voicesInitialized = false;
  private readonly voicesChangedHandler = () => this.updateSpeechVoices();
  languageLabel = 'English (EN)';
  @Output() sidebarToggle = new EventEmitter<void>();
  expansSupportMenu: boolean = false;
  searchModal: boolean = false;
  unreadCount: number = 0;
  @Output() menuClick = new EventEmitter<void>();
  // MVP1 flags
  flags = {
    directoryEnabled: environment.mVP1Settings?.directoryEnabled ?? true,
    searchEnabled: true //environment.mVP1Settings?.searchEnabled ?? true,
  };
  accessibilitySettings: AccessibilitySettings = { ...this.defaultAccessibilitySettings };
  isReading = false;
  isPaused = false;
  readingSource: 'screen' | 'selection' | null = null;
  selectionReaderVisible = false;
  selectionReaderText = '';
  selectionReaderPosition: { top: number; left: number } = { top: 0, left: 0 };
  selectionReaderWidth = 320;
  readonly isSpeechSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;
  @ViewChild('selectionReaderPanel') private selectionReaderPanel?: ElementRef<HTMLDivElement>;
  private readonly selectionMinChars = 3;
  private selectionChangeTimeoutId: number | null = null;
  private interactingWithSelectionReader = false;
  private readonly selectionReaderLabels = {
    en: {
      heading: 'Read selected text',
      play: 'Listen',
      resume: 'Resume',
      pause: 'Pause',
      close: 'Close',
      unsupported: 'Audio playback is not supported in this browser.',
    },
    ar: {
      heading: 'قراءة النص المحدد',
      play: 'يستمع',
      resume: 'استئناف',
      pause: 'إيقاف مؤقت',
      close: 'إغلاق',
      unsupported: 'التشغيل الصوتي غير مدعوم في هذا المتصفح.',
    },
  };
  private selectionRangeSnapshot?: Range;
  private selectionHighlightContainer?: HTMLDivElement;
  private selectionHighlightRects: HTMLDivElement[] = [];
  private selectionHighlightActive = false;
  private navigationSubscription?: Subscription;
  private userProfileSubscription?: Subscription;

  // This method is used to initialize the class instance using router, translate, and auth service
  constructor(
    private router: Router,
    public translate: TranslateService,
    private authService: AuthService,
    private sharedService: SharedService,
    private notificationHistoryService: NotificationHistoryService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.selectedLang = localStorage.getItem("selectedLang");
    this.switchLang(this.selectedLang);
    this.user = this.authService.getUserClaims();
    this.loadAccessibilitySettings();
    this.initializeSpeechVoices();
    this.loadUnreadCount();
    this.userProfileSubscription = this.sharedService.userProfile$.subscribe((profile) => {
      const loginName = profile?.['Login Name'] ?? '';
      if (loginName) {
        this.loadUnreadCount();
      } else {
        this.unreadCount = 0;
      }
    });
    this.navigationSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd && event.urlAfterRedirects === `/${RoutesPath.DASHBOARD}`) {
        this.loadUnreadCount();
      }
    });
  }

  get isPlatinumTheme(): boolean {
    return !!this.document?.body?.classList.contains('platinum-theme');
  }

  get currentLogo(): string {
    return this.isPlatinumTheme ? '/images/logo-navy.png' : '/images/logo.png';
  }

  getIconPath(icon: string): string {
    const fileName = icon?.split('/').pop() ?? icon;
    return this.isPlatinumTheme ? `/icons/platinum/${fileName}` : `/icons/${icon}`;
  }

  @HostListener('document:selectionchange')
  onDocumentSelectionChange() {
    if (typeof window === 'undefined') {
      return;
    }
    if (this.selectionChangeTimeoutId !== null) {
      window.clearTimeout(this.selectionChangeTimeoutId);
    }
    this.selectionChangeTimeoutId = window.setTimeout(() => this.updateSelectionReader(), 120);
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onViewportChanged() {
    if (!this.selectionReaderVisible) {
      return;
    }
    this.updateSelectionReader(true);
    this.updateSelectionHighlightOverlay();
  }

  @HostListener('document:pointerdown', ['$event'])
  onPointerDown(event: PointerEvent) {
    if (!this.selectionReaderVisible) {
      return;
    }
    const target = event.target as Node | null;
    if (target && this.selectionReaderPanel?.nativeElement.contains(target)) {
      this.interactingWithSelectionReader = true;
      return;
    }
    if (!this.isReadingSelectionActive()) {
      this.hideSelectionReader();
    }
  }

  @HostListener('document:pointerup')
  onPointerUp() {
    if (!this.selectionReaderVisible) {
      return;
    }
    this.interactingWithSelectionReader = false;
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.selectionReaderVisible) {
      this.hideSelectionReader(true);
    }
  }

  // This method is used to log out
  logout() {
    this.authService.logout();
  }

  // This method is used to switch lang using lang and clicked
  switchLang(lang: string, clicked = false) {
    lang = lang || 'en';
    const browserLang: any = this.translate.getBrowserLang();
    // this.translate.use(browserLang.match(/en|ar/) ? browserLang : 'en');

    this.selectedLang = lang;
    this.translate.use(lang);
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    localStorage.setItem("selectedLang", lang);
    this.updateLanguageLabel(this.selectedLang);
    this.initializeSpeechVoices();

    if (clicked) {
      // Refresh the current route to trigger re-initialization and API call
      const currentUrl = this.router.url;
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate([currentUrl]);
      });
    }
  }

  // This method is used to toggle sidebar
  toggleSidebar() {
    this.sidebarToggle.emit();
  }
  // This method is used to goto notifications
  gotoNotifications() {
    this.router.navigate([RoutesPath.NOTIFICATIONS])
  }
  // This method is used to goto dashboard
  gotoDashboard() {
    this.router.navigate([RoutesPath.DASHBOARD]);
  }
  // This method is used to goto faq
  gotoFaq() {
    this.router.navigate([RoutesPath.FAQ])
  }
  // This method is used to goto directory
  gotoDirectory() {
    this.router.navigate([RoutesPath.DIRECTORY])
  }
  // This method is used to goto setting
  gotoSetting() {
    this.router.navigate([RoutesPath.PROFILE])
  }

  increaseFont() {
    if (this.accessibilitySettings.fontScale >= 130) {
      return;
    }
    this.updateAccessibilitySettings({ fontScale: this.accessibilitySettings.fontScale + 10 });
  }

  decreaseFont() {
    if (this.accessibilitySettings.fontScale <= 80) {
      return;
    }
    this.updateAccessibilitySettings({ fontScale: this.accessibilitySettings.fontScale - 10 });
  }

  toggleHighContrast() {
    this.updateAccessibilitySettings({ highContrast: !this.accessibilitySettings.highContrast });
  }

  toggleGrayscale() {
    this.updateAccessibilitySettings({ grayscale: !this.accessibilitySettings.grayscale });
  }

  toggleUnderlineLinks() {
    this.updateAccessibilitySettings({ underlineLinks: !this.accessibilitySettings.underlineLinks });
  }

  increaseTextSpacing() {
    const next = this.getNextLevel(this.accessibilitySettings.textSpacing, this.textSpacingLevels, 1);
    if (next) {
      this.updateAccessibilitySettings({ textSpacing: next });
    }
  }

  decreaseTextSpacing() {
    const prev = this.getNextLevel(this.accessibilitySettings.textSpacing, this.textSpacingLevels, -1);
    if (prev) {
      this.updateAccessibilitySettings({ textSpacing: prev });
    }
  }

  increaseLineSpacing() {
    const next = this.getNextLevel(this.accessibilitySettings.lineSpacing, this.lineSpacingLevels, 1);
    if (next) {
      this.updateAccessibilitySettings({ lineSpacing: next });
    }
  }

  decreaseLineSpacing() {
    const prev = this.getNextLevel(this.accessibilitySettings.lineSpacing, this.lineSpacingLevels, -1);
    if (prev) {
      this.updateAccessibilitySettings({ lineSpacing: prev });
    }
  }

  toggleFontWeight() {
    const next: FontWeightOption = this.accessibilitySettings.fontWeight === 'bold' ? 'normal' : 'bold';
    this.updateAccessibilitySettings({ fontWeight: next });
  }

  startReadingSelection(text?: string, source: 'screen' | 'selection' = 'screen') {
    if (!this.isSpeechSupported || !this.speechSynthesisRef) {
      return;
    }
    const selectionText = (text ?? this.getCurrentSelectionText()).trim();
    const fallbackText = source === 'selection' ? '' : this.getFallbackReadableText();
    const rawText = selectionText || fallbackText;
    if (!rawText) {
      return;
    }
    const truncatedText = rawText.slice(0, 5000);
    this.stopReading();
    const utterance = new SpeechSynthesisUtterance(truncatedText);
    utterance.lang = this.selectedLang === 'ar' ? 'ar-SA' : 'en-US';
    const preferredVoice = this.getVoiceForLang(utterance.lang);
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }
    utterance.onend = () => this.onSpeechFinished();
    utterance.onerror = () => this.onSpeechFinished();
    this.speechUtterance = utterance;
    this.isReading = true;
    this.isPaused = false;
    this.readingSource = source;
    if (source === 'selection') {
      this.selectionReaderText = truncatedText;
      if (!this.selectionReaderVisible) {
        this.selectionReaderVisible = true;
      }
      this.applySelectionHighlight();
    }
    this.speechSynthesisRef.speak(utterance);
  }

  stopReading() {
    if (!this.isSpeechSupported || !this.speechSynthesisRef) {
      return;
    }
    if (this.speechSynthesisRef.speaking || this.speechSynthesisRef.pending) {
      this.speechSynthesisRef.cancel();
    }
    this.isReading = false;
    this.isPaused = false;
    this.speechUtterance = undefined;
    const wasSelectionSource = this.readingSource === 'selection';
    this.readingSource = null;
    if (wasSelectionSource) {
      this.selectionReaderVisible = false;
      this.selectionReaderText = '';
      this.clearSelectionHighlight();
    }
  }

  onSelectionPlay() {
    if (!this.isSpeechSupported) {
      return;
    }
    if (this.readingSource === 'selection' && this.isPaused) {
      this.resumeReading();
      return;
    }
    if (this.selectionReaderText) {
      this.startReadingSelection(this.selectionReaderText, 'selection');
    } else {
      this.startReadingSelection(undefined, 'selection');
    }
    this.applySelectionHighlight();
  }

  onSelectionPause() {
    if (this.readingSource === 'selection' && this.isReading && !this.isPaused) {
      this.pauseReading();
    }
  }

  onSelectionClose() {
    if (this.readingSource === 'selection') {
      this.stopReading();
    }
    this.hideSelectionReader();
    this.clearSelectionHighlight();
  }

  get selectionReaderStrings() {
    const lang = (this.selectedLang || 'en').toLowerCase();
    return lang === 'ar' ? this.selectionReaderLabels.ar : this.selectionReaderLabels.en;
  }

  private pauseReading() {
    if (!this.isSpeechSupported || !this.speechSynthesisRef) {
      return;
    }
    if (this.speechSynthesisRef.speaking && !this.speechSynthesisRef.paused) {
      this.speechSynthesisRef.pause();
      this.isPaused = true;
    }
  }

  private resumeReading() {
    if (!this.isSpeechSupported || !this.speechSynthesisRef) {
      return;
    }
    if (this.speechSynthesisRef.paused) {
      this.speechSynthesisRef.resume();
      this.isPaused = false;
      this.applySelectionHighlight();
    }
  }

  private getCurrentSelectionText(): string {
    if (typeof window === 'undefined') {
      return '';
    }
    const selection = window.getSelection();
    return selection ? selection.toString() : '';
  }

  private getFallbackReadableText(): string {
    if (typeof document === 'undefined') {
      return '';
    }
    const mainContent = document.querySelector('main');
    if (mainContent?.textContent) {
      return mainContent.textContent.trim();
    }
    return document.body?.innerText?.trim() ?? '';
  }

  private hideSelectionReader(stopSpeech = false) {
    if (stopSpeech && this.readingSource === 'selection') {
      this.stopReading();
      return;
    }
    this.selectionReaderVisible = false;
    if (!this.isReadingSelectionActive()) {
      this.selectionReaderText = '';
    }
    if (!this.isReadingSelectionActive()) {
      this.clearSelectionHighlight();
    }
  }

  private updateSelectionReader(onlyPosition = false) {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      if (!onlyPosition && !this.shouldHoldSelectionMenu()) {
        this.hideSelectionReader();
      }
      return;
    }
    if (this.isSelectionInsideReader(selection)) {
      return;
    }
    const text = selection.toString().trim();
    const range = selection.getRangeAt(0);
    const rect = this.getSelectionRect(range);
    const hasValidSelection = !selection.isCollapsed && text.length >= this.selectionMinChars && rect !== null;
    if (!hasValidSelection) {
      if (!onlyPosition && !this.shouldHoldSelectionMenu()) {
        this.hideSelectionReader();
      }
      return;
    }
    if (!rect) {
      return;
    }
    this.selectionReaderText = text.slice(0, 5000);
    this.selectionReaderPosition = this.computeSelectionMenuPosition(rect);
    this.selectionReaderVisible = true;
    this.selectionRangeSnapshot = range.cloneRange();
    if (this.selectionHighlightActive) {
      this.updateSelectionHighlightOverlay();
    }
  }

  private getSelectionRect(range: Range): DOMRect | null {
    const rect = range.getBoundingClientRect();
    if (rect && (rect.width !== 0 || rect.height !== 0)) {
      return rect;
    }
    const clientRects = range.getClientRects();
    if (clientRects.length > 0) {
      return clientRects[0];
    }
    return null;
  }

  private computeSelectionMenuPosition(rect: DOMRect): { top: number; left: number } {
    if (typeof window === 'undefined') {
      return { top: rect.bottom, left: rect.left };
    }
    const viewportPadding = 12;
    const maxWidth = 320;
    const preferredMinWidth = 220;
    const availableWidth = Math.max(0, window.innerWidth - viewportPadding * 2);
    let width = Math.min(maxWidth, Math.max(preferredMinWidth, availableWidth));
    const widthLimit = Math.max(80, window.innerWidth - viewportPadding);
    width = Math.min(width, widthLimit);
    if (window.innerWidth > 480) {
      width = Math.min(widthLimit, Math.max(width, 200));
    }
    this.selectionReaderWidth = width;
    const menuHeight = 136;
    let top = rect.bottom + 12;
    if (top + menuHeight > window.innerHeight - viewportPadding) {
      top = rect.top - menuHeight - 12;
    }
    if (top < viewportPadding) {
      top = viewportPadding;
    }
    let left = rect.left + rect.width / 2 - this.selectionReaderWidth / 2;
    const maxLeft = window.innerWidth - this.selectionReaderWidth - viewportPadding;
    if (left < viewportPadding) {
      left = viewportPadding;
    } else if (left > maxLeft) {
      left = Math.max(viewportPadding, maxLeft);
    }
    return { top, left };
  }

  private shouldHoldSelectionMenu(): boolean {
    return this.interactingWithSelectionReader || this.isReadingSelectionActive();
  }

  private initializeSpeechVoices() {
    if (!this.isSpeechSupported || !this.speechSynthesisRef) {
      return;
    }
    this.updateSpeechVoices();
    if (!this.voicesInitialized) {
      if (typeof (this.speechSynthesisRef as unknown as EventTarget).addEventListener === 'function') {
        (this.speechSynthesisRef as unknown as EventTarget).addEventListener('voiceschanged', this.voicesChangedHandler);
      } else {
        this.speechSynthesisRef.onvoiceschanged = this.voicesChangedHandler;
      }
      this.voicesInitialized = true;
    }
  }

  private updateSpeechVoices() {
    if (!this.isSpeechSupported || !this.speechSynthesisRef) {
      return;
    }
    const voices = this.speechSynthesisRef.getVoices();
    if (voices.length) {
      this.speechVoices = voices;
    }
  }

  private getVoiceForLang(lang: string): SpeechSynthesisVoice | undefined {
    if (!this.speechVoices.length) {
      this.updateSpeechVoices();
    }
    if (!this.speechVoices.length) {
      return undefined;
    }
    const normalized = lang.toLowerCase();
    const exact = this.speechVoices.find((voice) => voice.lang?.toLowerCase() === normalized);
    if (exact) {
      return exact;
    }
    const prefix = normalized.split('-')[0];
    return this.speechVoices.find((voice) => voice.lang?.toLowerCase().startsWith(prefix));
  }

  private isSelectionInsideReader(selection: Selection): boolean {
    if (!this.selectionReaderPanel) {
      return false;
    }
    const nodeA = selection.anchorNode;
    const nodeB = selection.focusNode;
    const element = this.selectionReaderPanel.nativeElement;
    return (!!nodeA && element.contains(nodeA)) || (!!nodeB && element.contains(nodeB));
  }

  private isReadingSelectionActive(): boolean {
    return this.readingSource === 'selection' && (this.isReading || this.isPaused);
  }

  private applySelectionHighlight() {
    if (typeof document === 'undefined' || !this.selectionRangeSnapshot) {
      return;
    }
    const range = this.selectionRangeSnapshot.cloneRange();
    const rects = Array.from(range.getClientRects());
    if (rects.length === 0) {
      this.clearSelectionHighlight();
      return;
    }
    if (!this.selectionHighlightContainer) {
      this.selectionHighlightContainer = document.createElement('div');
      this.selectionHighlightContainer.className = 'a11y-selection-audio-highlight-container';
      document.body.appendChild(this.selectionHighlightContainer);
    }
    this.selectionHighlightContainer.hidden = false;
    this.selectionHighlightActive = true;
    this.renderSelectionHighlight(rects);
  }

  private renderSelectionHighlight(rects: DOMRect[]) {
    const container = this.selectionHighlightContainer;
    if (!container) {
      return;
    }
    while (this.selectionHighlightRects.length) {
      const rectEl = this.selectionHighlightRects.pop();
      rectEl?.remove();
    }
    rects.forEach((rect) => {
      const highlight = document.createElement('div');
      highlight.className = 'a11y-selection-audio-highlight';
      highlight.style.top = `${Math.max(0, rect.top)}px`;
      highlight.style.left = `${Math.max(0, rect.left)}px`;
      highlight.style.width = `${Math.max(0, rect.width)}px`;
      highlight.style.height = `${Math.max(0, rect.height)}px`;
      this.selectionHighlightRects.push(highlight);
      container.appendChild(highlight);
    });
  }

  private updateSelectionHighlightOverlay() {
    if (!this.selectionHighlightActive || !this.selectionRangeSnapshot) {
      return;
    }
    const rects = Array.from(this.selectionRangeSnapshot.cloneRange().getClientRects());
    if (!rects.length) {
      this.clearSelectionHighlight();
      return;
    }
    this.renderSelectionHighlight(rects);
  }

  private clearSelectionHighlight() {
    this.selectionHighlightActive = false;
    while (this.selectionHighlightRects.length) {
      const highlight = this.selectionHighlightRects.pop();
      highlight?.remove();
    }
    if (this.selectionHighlightContainer) {
      this.selectionHighlightContainer.hidden = true;
    }
  }

  resetAccessibility() {
    this.stopReading();
    this.updateAccessibilitySettings({ ...this.defaultAccessibilitySettings });
  }

  toggleReadingGuide() {
    this.updateAccessibilitySettings({ readingGuide: !this.accessibilitySettings.readingGuide });
  }

  toggleBigCursor() {
    this.updateAccessibilitySettings({ bigCursor: !this.accessibilitySettings.bigCursor });
  }

  ngOnDestroy(): void {
    this.navigationSubscription?.unsubscribe();
    this.userProfileSubscription?.unsubscribe();
    this.disableReadingGuide();
    this.stopReading();
    if (typeof window !== 'undefined' && this.selectionChangeTimeoutId !== null) {
      window.clearTimeout(this.selectionChangeTimeoutId);
      this.selectionChangeTimeoutId = null;
    }
    if (this.speechSynthesisRef) {
      const target = this.speechSynthesisRef as unknown as EventTarget;
      if (typeof target.removeEventListener === 'function') {
        target.removeEventListener('voiceschanged', this.voicesChangedHandler);
      } else if (this.speechSynthesisRef.onvoiceschanged === this.voicesChangedHandler) {
        this.speechSynthesisRef.onvoiceschanged = null;
      }
    }
  }

  private loadAccessibilitySettings() {
    try {
      const stored = localStorage.getItem(this.accessibilityStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.accessibilitySettings = {
          fontScale: this.validateFontScale(parsed.fontScale),
          highContrast: Boolean(parsed.highContrast),
          grayscale: Boolean(parsed.grayscale),
          underlineLinks: Boolean(parsed.underlineLinks),
          readingGuide: Boolean(parsed.readingGuide),
          bigCursor: Boolean(parsed.bigCursor),
          textSpacing: this.validateTextSpacing(parsed.textSpacing),
          lineSpacing: this.validateLineSpacing(parsed.lineSpacing),
          fontWeight: this.validateFontWeight(parsed.fontWeight),
        };
      }
    } catch {
      this.accessibilitySettings = { ...this.defaultAccessibilitySettings };
    } finally {
      this.applyAccessibilitySettings();
    }
  }

  private validateFontScale(value: number) {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return 100;
    }
    return Math.min(130, Math.max(80, Math.round(value / 10) * 10));
  }

  private updateAccessibilitySettings(partial: Partial<AccessibilitySettings>) {
    this.accessibilitySettings = {
      ...this.accessibilitySettings,
      ...partial,
    };
    this.applyAccessibilitySettings();
    this.persistAccessibilitySettings();
  }

  private applyAccessibilitySettings() {
    if (!this.html) {
      return;
    }
    this.html.style.fontSize = `${this.accessibilitySettings.fontScale}%`;
    this.html.classList.toggle('a11y-high-contrast', this.accessibilitySettings.highContrast);
    this.html.classList.toggle('a11y-grayscale', this.accessibilitySettings.grayscale);
    this.html.classList.toggle('a11y-underline-links', this.accessibilitySettings.underlineLinks);
    this.html.classList.toggle('a11y-big-cursor', this.accessibilitySettings.bigCursor);
    this.html.classList.toggle('a11y-font-bold', this.fontWeightClass[this.accessibilitySettings.fontWeight]);
    const spacing = this.textSpacingStyles[this.accessibilitySettings.textSpacing];
    this.html.style.setProperty('--a11y-letter-spacing', spacing.letterSpacing);
    this.html.style.setProperty('--a11y-word-spacing', spacing.wordSpacing);
    this.html.style.setProperty('--a11y-line-height', this.lineSpacingStyles[this.accessibilitySettings.lineSpacing]);
    if (this.accessibilitySettings.readingGuide) {
      this.enableReadingGuide();
    } else {
      this.disableReadingGuide();
    }
  }

  private persistAccessibilitySettings() {
    try {
      localStorage.setItem(this.accessibilityStorageKey, JSON.stringify(this.accessibilitySettings));
    } catch {
      // Storage may be unavailable (private mode, quotas). Ignore.
    }
  }

  private enableReadingGuide() {
    if (!this.readingGuideOverlay) {
      this.readingGuideOverlay = document.createElement('div');
      this.readingGuideOverlay.className = 'a11y-reading-guide-overlay';
      this.readingGuideHighlight = document.createElement('div');
      this.readingGuideHighlight.className = 'a11y-reading-guide-highlight';
      this.readingGuideOverlay.appendChild(this.readingGuideHighlight);
      document.body.appendChild(this.readingGuideOverlay);
    }
    if (!this.readingGuideHighlight) {
      return;
    }
    this.readingGuideOverlay!.hidden = false;
    this.readingGuideHighlight.hidden = false;
    const guideHeight = this.readingGuideHighlight.offsetHeight || 160;
    const initialTop = Math.max(0, (window.innerHeight - guideHeight) / 2);
    this.readingGuideHighlight.style.transform = `translateY(${initialTop}px)`;
    if (!this.pointerMoveHandler) {
      this.pointerMoveHandler = (event: PointerEvent) => {
        if (event.pointerType === 'touch') {
          return;
        }
        if (!this.readingGuideHighlight) {
          return;
        }
        const halfHeight = this.readingGuideHighlight.offsetHeight / 2 || 80;
        const top = Math.max(0, event.clientY - halfHeight);
        this.readingGuideHighlight.style.transform = `translateY(${top}px)`;
      };
      window.addEventListener('pointermove', this.pointerMoveHandler, { passive: true });
    }
  }

  private disableReadingGuide() {
    if (this.readingGuideOverlay) {
      this.readingGuideOverlay.hidden = true;
    }
    if (this.readingGuideHighlight) {
      this.readingGuideHighlight.hidden = true;
    }
    if (this.pointerMoveHandler) {
      window.removeEventListener('pointermove', this.pointerMoveHandler);
      this.pointerMoveHandler = undefined;
    }
  }

  private getNextLevel<T extends string>(current: T, levels: T[], step: 1 | -1): T | null {
    const index = levels.indexOf(current);
    if (index === -1) {
      return levels[0];
    }
    const nextIndex = index + step;
    if (nextIndex < 0 || nextIndex >= levels.length) {
      return null;
    }
    return levels[nextIndex];
  }

  private validateTextSpacing(value: unknown): TextSpacingLevel {
    return this.textSpacingLevels.includes(value as TextSpacingLevel) ? (value as TextSpacingLevel) : 'normal';
  }

  private validateLineSpacing(value: unknown): LineSpacingLevel {
    return this.lineSpacingLevels.includes(value as LineSpacingLevel) ? (value as LineSpacingLevel) : 'normal';
  }

  private validateFontWeight(value: unknown): FontWeightOption {
    return value === 'bold' ? 'bold' : 'normal';
  }

  private onSpeechFinished() {
    this.isReading = false;
    this.isPaused = false;
    this.speechUtterance = undefined;
    const wasSelectionSource = this.readingSource === 'selection';
    this.readingSource = null;
    if (wasSelectionSource && !this.interactingWithSelectionReader) {
      this.selectionReaderVisible = false;
      this.selectionReaderText = '';
    }
  }

  private updateLanguageLabel(lang: string | null) {
    const normalized = (lang || 'en').toLowerCase();
    this.languageLabel = normalized === 'ar' ? 'العربية (AR)' : 'English (EN)';
  }
  private loadUnreadCount() {
    const loginName = this.sharedService.getUserProfile()?.['Login Name'] ?? '';
    if (!loginName) {
      this.unreadCount = 0;
      return;
    }

    this.notificationHistoryService.getUnreadCount(loginName).subscribe({
      next: (count) => {
        this.unreadCount = count;
      },
      error: () => {
        this.unreadCount = 0;
      }
    });
  }

  gotoContactUs() {
    this.router.navigate([RoutesPath.CONTACT_US]);
    // window.open('https://www.dubaichambers.com/en/contact-us', '_blank');
  }

  get selectedPrimaryMemberDisplay(): string {
    const account = this.sharedService.getPrimaryAccount() || {};
    let memberName = '';
    let csn = account?.['CSN'] ?? '';

    if (this.selectedLang === 'ar') {
      if (account?.['DC Name Arabic']) {
        memberName = account?.['DC Name Arabic'];
      } else {
        memberName = account?.['DC Name English'] ?? '';
      }
    } else {
      memberName = account?.['DC Name English'] ?? '';
    }

    if (!memberName && !csn) {
      return '';
    }
    memberName = memberName.length > 60 ? memberName.substring(0, 60) + '...' : memberName;
    if (!csn) {
      return memberName;
    }
    return `${memberName} (${csn})`;
  }
}
